import { DisciplineEnum, DisciplineOptions, FrequencyStatusEnum, FrequencyTypeEnum, type CertificationPeriodViewModel } from '@generated';
import { useSearch } from '~/util/http/useSearch';
import { combineSieveValues } from '~/util/sieve/filter';
import { useSuspenseSearch } from '~/util/http/useSuspenseSearch';

function getFrequencyStatusFilter(frequencyStatuses: FrequencyStatusGroups[]) {
	const statuses: number[] = [];
	if (frequencyStatuses.includes('scheduled')) {
		statuses.push(FrequencyStatusEnum.NeedsPerformed);
		statuses.push(FrequencyStatusEnum.NeedsStaffed);
	}
	if (frequencyStatuses.includes('performing')) {
		statuses.push(FrequencyStatusEnum.Performing);
	}
	if (frequencyStatuses.includes('performed')) {
		statuses.push(FrequencyStatusEnum.Performed);
	}
	if (frequencyStatuses.includes('discontinued')) {
		statuses.push(FrequencyStatusEnum.Discontinued);
	}
	if (frequencyStatuses.includes('missed')) {
		statuses.push(FrequencyStatusEnum.Missed);
	}
	return combineSieveValues(statuses);
}

type FrequencyStatusGroups = 'scheduled' | 'performing' | 'performed' | 'discontinued' | 'missed';

type AdditionalEventTypes = 'physician-appointments';

export interface SearchCertificationPeriodScheduleOptions {
	frequencyTypes: FrequencyTypeEnum[];
	frequencyStatuses: FrequencyStatusGroups[];
	additionalEventTypes?: AdditionalEventTypes[];
	disciplines?: DisciplineEnum[];
}

export interface useSearchCertificationPeriodScheduleProps {
	certificationPeriod: CertificationPeriodViewModel;
	options?: SearchCertificationPeriodScheduleOptions;
}

export function useSearchCertificationPeriodSchedule({ certificationPeriod, options }: useSearchCertificationPeriodScheduleProps) {
	const frequencyTypeFilter = combineSieveValues(options?.frequencyTypes ?? [FrequencyTypeEnum.Routine, FrequencyTypeEnum.Evaluation]);
	const frequencyStatusFilter = getFrequencyStatusFilter(options?.frequencyStatuses ?? ['scheduled', 'performing', 'performed']);
	const disciplineFilter = combineSieveValues(options?.disciplines ?? DisciplineOptions.map(x => x.id));

	const { data: frequencies, isLoading: frequenciesLoading } = useSearch({
		searchType: 'frequency',
		filters: [
			{ name: 'certificationPeriodId', operator: '==', value: certificationPeriod.id },
			{ name: 'frequencyTypeId', operator: '==', value: frequencyTypeFilter },
			{ name: 'frequencyStatusId', operator: '==', value: frequencyStatusFilter },
			{ name: 'disciplineId', operator: '==', value: disciplineFilter }
		]
	});

	const physicianAppointmentsEnabled = !!options?.additionalEventTypes?.includes('physician-appointments');

	const { data: physicianAppointments, isLoading: physicianAppointmentsLoading } = useSearch({
		searchType: 'patient-physician-appointment',
		filters: [
			{ name: 'appointmentDateAndTime', operator: '>=', value: certificationPeriod.startDate },
			{ name: 'appointmentDateAndTime', operator: '<=', value: certificationPeriod.endDate }
		],
		enabled: physicianAppointmentsEnabled
	});

	if (frequenciesLoading || (physicianAppointmentsLoading && physicianAppointmentsEnabled)) {
		return {
			frequencies: [],
			isLoading: true
		};
	}

	return {
		frequencies,
		isLoading: false
	};
}

export function useGetCertificationPeriodSchedule({ certificationPeriod }: { certificationPeriod: CertificationPeriodViewModel }) {
	return useSuspenseSearch({
		searchType: 'frequency',
		filters: [
			{ name: 'certificationPeriodId', operator: '==', value: certificationPeriod.id },
			{
				name: 'frequencyStatusId',
				operator: '!=',
				value: FrequencyStatusEnum.Drafted
			},
			{
				name: 'frequencyStatusId',
				operator: '!=',
				value: FrequencyStatusEnum.DiscontinueDrafted
			},
			{
				name: 'frequencyStatusId',
				operator: '!=',
				value: FrequencyStatusEnum.Discontinued
			}
		],
		pageSize: 1000
	});
}
